﻿import { Effect } from 'effect';

/** Parser HTTP exchanges return finite, buffered responses. Own the transport
 * and reader until buffering ends so subsequent JSON/bytes consumers cannot
 * outlive their request. This also discards responses arriving after abort.
 */
export function bufferedParserFetchEffect<E>(
  fetchImplementation: typeof globalThis.fetch,
  url: URL,
  init: RequestInit | undefined,
  onError: (cause: unknown, stage: 'request' | 'body') => E,
): Effect.Effect<Response, E> {
  return Effect.acquireUseRelease(
    Effect.sync(() => ({
      controller: new AbortController(),
      response: undefined as Response | undefined,
    })),
    (owner) =>
      Effect.gen(function* () {
        const signal = init?.signal
          ? AbortSignal.any([init.signal, owner.controller.signal])
          : owner.controller.signal;
        const response = yield* Effect.tryPromise({
          try: async () => {
            const response = await fetchImplementation(url, { ...init, signal });
            if (signal.aborted) {
              await response.body?.cancel().catch(() => undefined);
              throw new Error('Response arrived after cancellation');
            }
            owner.response = response;
            return response;
          },
          catch: (cause) => onError(cause, 'request'),
        });
        const bytes = response.body
          ? yield* Effect.acquireUseRelease(
              Effect.try({
                try: () => response.body!.getReader(),
                catch: (cause) => onError(cause, 'body'),
              }),
              (reader) =>
                Effect.gen(function* () {
                  const chunks: Uint8Array[] = [];
                  let length = 0;
                  while (true) {
                    const chunk = yield* Effect.tryPromise({
                      try: () => reader.read(),
                      catch: (cause) => onError(cause, 'body'),
                    });
                    if (chunk.done) break;
                    chunks.push(chunk.value);
                    length += chunk.value.length;
                  }
                  const bytes = new Uint8Array(length);
                  let offset = 0;
                  for (const chunk of chunks) {
                    bytes.set(chunk, offset);
                    offset += chunk.length;
                  }
                  return bytes;
                }),
              (reader) =>
                Effect.tryPromise({
                  try: () => reader.cancel(),
                  catch: (cause) => onError(cause, 'body'),
                }).pipe(
                  Effect.catch(() => Effect.void),
                  Effect.ensuring(Effect.sync(() => reader.releaseLock())),
                ),
            )
          : null;
        return new Response(bytes, {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        });
      }),
    (owner) =>
      Effect.gen(function* () {
        owner.controller.abort();
        const body = owner.response?.body;
        if (body && !body.locked)
          yield* Effect.tryPromise({
            try: () => body.cancel(),
            catch: (cause) => onError(cause, 'body'),
          }).pipe(Effect.catch(() => Effect.void));
      }),
  );
}
